import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { OwnerFinanceDialog } from "./center/OwnerQuickDialogs";

const ACCOUNT_TYPES = [
  ["bank", "Banco"], ["wallet", "Billetera"], ["cash", "Efectivo"],
  ["investment", "Inversión"], ["receivable", "Por cobrar"], ["debt", "Deuda"], ["other", "Otra"],
];
const CATEGORY_KINDS = [["expense", "Gasto"], ["income", "Ingreso"], ["both", "Ingreso y gasto"]];

function idOf(value) { return String(value?.id || value?._id || value || ""); }
function money(value, currency = "ARS") {
  try { return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0)); }
  catch { return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`; }
}
function dateTimeLocal(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function displayDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "short", timeStyle: "short" }).format(date);
}
function typeLabel(type) {
  return ({ expense: "Gasto", income: "Ingreso", transfer: "Transferencia", adjustment: "Ajuste" })[type] || type;
}

function transactionTouchesAccount(row, accountId) {
  if (!accountId) return true;
  return [row.accountId, row.fromAccountId, row.toAccountId].some((value) => idOf(value) === accountId);
}

function impactForAccount(row, accountId, fallbackCurrency = "ARS") {
  if (!accountId || !transactionTouchesAccount(row, accountId)) return null;
  if (row.type === "transfer") {
    if (idOf(row.fromAccountId) === accountId) return { amount: -Number(row.amount || 0), currency: row.currency || fallbackCurrency };
    if (idOf(row.toAccountId) === accountId) return { amount: Number(row.toAmount || row.amount || 0), currency: row.toCurrency || fallbackCurrency };
    return null;
  }
  if (idOf(row.accountId) !== accountId) return null;
  if (row.type === "expense") return { amount: -Number(row.amount || 0), currency: row.currency || fallbackCurrency };
  if (row.type === "income") return { amount: Number(row.amount || 0), currency: row.currency || fallbackCurrency };
  if (row.type === "adjustment") {
    return { amount: (row.adjustmentDirection === "decrease" ? -1 : 1) * Number(row.amount || 0), currency: row.currency || fallbackCurrency };
  }
  return { amount: Number(row.amount || 0), currency: row.currency || fallbackCurrency };
}

function signedMoney(impact) {
  if (!impact) return "—";
  const sign = impact.amount > 0 ? "+" : impact.amount < 0 ? "-" : "";
  return `${sign}${money(Math.abs(impact.amount), impact.currency)}`;
}

export default function OwnerMovementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = React.useState([]);
  const [transactions, setTransactions] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [detectedCategories, setDetectedCategories] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [movementOpen, setMovementOpen] = React.useState(false);
  const [editMovement, setEditMovement] = React.useState(null);
  const [movementForm, setMovementForm] = React.useState({ category: "", description: "", area: "Personal", occurredAt: "", accountId: "" });
  const [accountDialog, setAccountDialog] = React.useState(null);
  const [accountDeleteConfirm, setAccountDeleteConfirm] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState({ name: "", type: "bank", currency: "ARS", openingBalance: "", notes: "" });
  const [categoryDialog, setCategoryDialog] = React.useState(null);
  const [categoryForm, setCategoryForm] = React.useState({ name: "", kind: "expense", renameHistory: true, renameFrom: "" });
  const selectedType = String(searchParams.get("tipo") || searchParams.get("type") || "").trim();
  const selectedCategory = String(searchParams.get("categoria") || searchParams.get("category") || "").trim();
  const selectedFrom = String(searchParams.get("desde") || searchParams.get("from") || "").trim();
  const selectedTo = String(searchParams.get("hasta") || searchParams.get("to") || "").trim();
  const jarvisReview = searchParams.get("jarvisReview") === "1";

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [accountData, transactionData, categoryData] = await Promise.all([
        ownerApi.listFinanceAccounts({ includeArchived: true }),
        ownerApi.listFinanceTransactions({ limit: 500, type: selectedType, category: selectedCategory, from: selectedFrom, to: selectedTo }),
        ownerApi.listFinanceCategories(),
      ]);
      setAccounts(accountData.accounts || []);
      setTransactions(transactionData.transactions || []);
      setCategories(categoryData.categories || []);
      setDetectedCategories(categoryData.detected || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar movimientos y configuración."));
    }
  }, [selectedType, selectedCategory, selectedFrom, selectedTo]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const action = searchParams.get("jarvisAction");
    if (!action) return;
    if (action === "movement") setMovementOpen(true);
    if (action === "account") openNewAccount();
    if (action === "category") openNewCategory();
    const next = new URLSearchParams(searchParams);
    next.delete("jarvisAction");
    setSearchParams(next, { replace: true });
  // openNewAccount/openNewCategory only initialize local dialog state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const activeAccounts = React.useMemo(() => accounts.filter((row) => row.archived !== true), [accounts]);
  const accountMap = React.useMemo(() => new Map(accounts.map((row) => [idOf(row), row])), [accounts]);
  const selectedAccountId = String(searchParams.get("cuenta") || "").trim();
  const selectedAccount = selectedAccountId ? accountMap.get(selectedAccountId) : null;
  const categoryNames = React.useMemo(() => {
    const names = new Set();
    categories.forEach((row) => names.add(String(row.name || "").trim()));
    detectedCategories.forEach((row) => names.add(String(row.name || "").trim()));
    transactions.forEach((row) => { if (row.category) names.add(String(row.category).trim()); });
    return [...names].filter(Boolean).sort((a, b) => a.localeCompare(b, "es"));
  }, [categories, detectedCategories, transactions]);

  const filteredTransactions = React.useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("es");
    return transactions.filter((row) => {
      if (selectedAccountId && !transactionTouchesAccount(row, selectedAccountId)) return false;
      if (selectedType && row.type !== selectedType) return false;
      if (selectedCategory && String(row.category || "").toLocaleLowerCase("es") !== selectedCategory.toLocaleLowerCase("es")) return false;
      if (!needle) return true;
      const account = accountMap.get(idOf(row.accountId));
      const from = accountMap.get(idOf(row.fromAccountId));
      const to = accountMap.get(idOf(row.toAccountId));
      return [row.description, row.category, row.area, row.currency, account?.name, from?.name, to?.name, typeLabel(row.type)]
        .filter(Boolean).join(" ").toLocaleLowerCase("es").includes(needle);
    });
  }, [transactions, query, accountMap, selectedAccountId, selectedType, selectedCategory]);

  const openMovementEditor = (row) => {
    setEditMovement(row);
    setMovementForm({
      category: row.category || "",
      description: row.description || "",
      area: row.area || "Personal",
      occurredAt: dateTimeLocal(row.occurredAt),
      accountId: idOf(row.accountId),
    });
  };

  const saveMovement = async () => {
    if (!editMovement) return;
    setSaving(true); setError("");
    try {
      await ownerApi.updateFinanceTransaction(idOf(editMovement), {
        category: movementForm.category,
        description: movementForm.description,
        area: movementForm.area,
        occurredAt: movementForm.occurredAt ? new Date(movementForm.occurredAt).toISOString() : undefined,
        ...(editMovement.type !== "transfer" && movementForm.accountId ? { accountId: movementForm.accountId } : {}),
      });
      setEditMovement(null);
      await load();
    } catch (err) { setError(formatApiError(err, "No pude editar el movimiento.")); }
    finally { setSaving(false); }
  };

  const openNewAccount = () => {
    setAccountDeleteConfirm(false);
    setAccountDialog({ mode: "create" });
    setAccountForm({ name: "", type: "bank", currency: "ARS", openingBalance: "", notes: "" });
  };
  const openEditAccount = (row) => {
    setAccountDeleteConfirm(false);
    setAccountDialog({ mode: "edit", row });
    setAccountForm({ name: row.name || "", type: row.type || "bank", currency: row.currency || "ARS", openingBalance: row.openingBalance ?? "", notes: row.notes || "" });
  };
  const saveAccount = async () => {
    if (!accountForm.name.trim()) return;
    setSaving(true); setError("");
    try {
      if (accountDialog?.mode === "edit") {
        await ownerApi.updateFinanceAccount(idOf(accountDialog.row), { name: accountForm.name, type: accountForm.type, notes: accountForm.notes });
      } else {
        await ownerApi.createFinanceAccount({ ...accountForm, openingBalance: Number(accountForm.openingBalance || 0) });
      }
      setAccountDialog(null);
      await load();
    } catch (err) { setError(formatApiError(err, "No pude guardar la cuenta.")); }
    finally { setSaving(false); }
  };

  const deleteAccount = async () => {
    const row = accountDialog?.mode === "edit" ? accountDialog.row : null;
    if (!row) return;
    setSaving(true); setError("");
    try {
      await ownerApi.deleteFinanceAccount(idOf(row));
      const deletedId = idOf(row);
      setAccountDeleteConfirm(false);
      setAccountDialog(null);
      if (String(searchParams.get("cuenta") || "") === deletedId) {
        const next = new URLSearchParams(searchParams);
        next.delete("cuenta");
        setSearchParams(next, { replace: true });
      }
      await load();
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar la cuenta."));
    } finally {
      setSaving(false);
    }
  };

  const openNewCategory = () => {
    setCategoryDialog({ mode: "create" });
    setCategoryForm({ name: "", kind: "expense", renameHistory: true, renameFrom: "" });
  };
  const openEditCategory = (row) => {
    setCategoryDialog({ mode: "edit", row });
    setCategoryForm({ name: row.name || "", kind: row.kind || "expense", renameHistory: true, renameFrom: "" });
  };
  const openDetectedCategory = (row) => {
    setCategoryDialog({ mode: "detected", row });
    setCategoryForm({ name: row.name || "", kind: row.kind || "expense", renameHistory: true, renameFrom: row.name || "" });
  };
  const saveCategory = async () => {
    if (!categoryForm.name.trim()) return;
    setSaving(true); setError("");
    try {
      if (categoryDialog?.mode === "edit") {
        await ownerApi.updateFinanceCategory(idOf(categoryDialog.row), {
          name: categoryForm.name,
          kind: categoryForm.kind,
          renameHistory: categoryForm.renameHistory,
        });
      } else {
        await ownerApi.createFinanceCategory({
          name: categoryForm.name,
          kind: categoryForm.kind,
          ...(categoryDialog?.mode === "detected" ? { renameFrom: categoryForm.renameFrom } : {}),
        });
      }
      setCategoryDialog(null);
      await load();
    } catch (err) { setError(formatApiError(err, "No pude guardar la categoría.")); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ pb: 1, maxWidth: 1660, mx: "auto" }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: .65 }}>
        <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} disabled={!activeAccounts.length} onClick={() => setMovementOpen(true)} sx={{ minHeight: 34, textTransform: "none", fontWeight: 950 }}>Registrar movimiento</Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {(selectedType || selectedCategory) ? <Alert severity={jarvisReview ? "info" : "success"} sx={{ mb: 1, py: .25 }} action={<Button size="small" onClick={() => { const next = new URLSearchParams(searchParams); ["tipo", "type", "categoria", "category", "desde", "from", "hasta", "to", "jarvisReview"].forEach((key) => next.delete(key)); setSearchParams(next, { replace: true }); }} sx={{ textTransform: "none", fontWeight: 900 }}>Quitar filtro</Button>}>
        {jarvisReview ? "Jarvis está trabajando sobre este lote · " : "Filtro activo · "}{selectedType === "income" ? "Entradas" : selectedType === "expense" ? "Gastos" : selectedType || "Movimientos"}{selectedCategory ? ` · ${selectedCategory}` : ""}{selectedFrom || selectedTo ? ` · ${selectedFrom || "…"} → ${selectedTo || "…"}` : ""} · {filteredTransactions.length} registros
      </Alert> : null}

      {selectedAccount ? (
        <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1.2}>
              <Box>
                <Typography sx={{ color: "text.secondary", fontSize: ".72rem", fontWeight: 900, textTransform: "uppercase" }}>Caja seleccionada</Typography>
                <Typography variant="h5" sx={{ fontWeight: 1000 }}>{selectedAccount.name}</Typography>
                <Typography sx={{ color: "text.secondary", mt: .25, fontSize: ".82rem" }}>El saldo sale del saldo inicial más el impacto neto de todos sus movimientos.</Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => { const next = new URLSearchParams(searchParams); next.delete("cuenta"); setSearchParams(next, { replace: true }); }}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Ver todas las cajas
              </Button>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" }, gap: 1, mt: 1.5 }}>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "action.hover" }}><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Saldo inicial</Typography><Typography sx={{ fontWeight: 1000 }}>{money(selectedAccount.openingBalance, selectedAccount.currency)}</Typography></Box>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "action.hover" }}><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Movimientos netos</Typography><Typography sx={{ fontWeight: 1000 }}>{signedMoney({ amount: Number(selectedAccount.balance || 0) - Number(selectedAccount.openingBalance || 0), currency: selectedAccount.currency })}</Typography></Box>
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "action.hover" }}><Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>Saldo actual</Typography><Typography sx={{ fontWeight: 1000 }}>{money(selectedAccount.balance, selectedAccount.currency)}</Typography></Box>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.6fr) minmax(330px,.75fr)" }, gap: 1 }}>
        <Card variant="outlined" sx={{ borderRadius: 3, minWidth: 0 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} sx={{ mb: 1.3 }}>
              <Stack direction="row" spacing={.7} alignItems="center"><ReceiptLongRoundedIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 950 }}>Tabla de movimientos</Typography><Chip size="small" label={filteredTransactions.length} /></Stack>
              <TextField size="small" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar movimiento, categoría o caja…" sx={{ minWidth: { sm: 290 } }} />
            </Stack>
            <Divider sx={{ mb: 1 }} />

            <Box sx={{ display: { xs: "grid", md: "none" }, gap: .8 }}>
              {filteredTransactions.map((row) => {
                const account = accountMap.get(idOf(row.accountId));
                const from = accountMap.get(idOf(row.fromAccountId));
                const to = accountMap.get(idOf(row.toAccountId));
                return <Box key={idOf(row)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.2, p: 1.1 }}>
                  <Stack direction="row" justifyContent="space-between" gap={1} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}><Typography sx={{ fontWeight: 950 }}>{row.description || row.category || typeLabel(row.type)}</Typography><Typography sx={{ fontSize: ".73rem", color: "text.secondary" }}>{displayDate(row.occurredAt)} · {typeLabel(row.type)}</Typography></Box>
                    <IconButton size="small" onClick={() => openMovementEditor(row)}><EditRoundedIcon fontSize="small" /></IconButton>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mt: .8 }}><Typography sx={{ fontSize: ".78rem", color: "text.secondary" }}>{row.type === "transfer" ? `${from?.name || "Origen"} → ${to?.name || "Destino"}` : account?.name || "Sin caja"}{row.category ? ` · ${row.category}` : ""}</Typography><Typography sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>{selectedAccountId ? signedMoney(impactForAccount(row, selectedAccountId, selectedAccount?.currency)) : `${row.type === "expense" ? "-" : row.type === "income" ? "+" : ""}${money(row.amount, row.currency)}`}</Typography></Stack>
                </Box>;
              })}
            </Box>

            <TableContainer sx={{ display: { xs: "none", md: "block" }, maxHeight: 660 }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell>Detalle</TableCell><TableCell>Categoría</TableCell><TableCell>Caja / cuenta</TableCell><TableCell align="right">{selectedAccountId ? "Impacto" : "Monto"}</TableCell><TableCell width={52}></TableCell></TableRow></TableHead>
                <TableBody>{filteredTransactions.map((row) => {
                  const account = accountMap.get(idOf(row.accountId));
                  const from = accountMap.get(idOf(row.fromAccountId));
                  const to = accountMap.get(idOf(row.toAccountId));
                  return <TableRow key={idOf(row)} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{displayDate(row.occurredAt)}</TableCell>
                    <TableCell><Typography sx={{ fontWeight: 900, fontSize: ".82rem" }}>{row.description || typeLabel(row.type)}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".7rem" }}>{typeLabel(row.type)}{row.area ? ` · ${row.area}` : ""}</Typography></TableCell>
                    <TableCell>{row.category || "—"}</TableCell>
                    <TableCell>{row.type === "transfer" ? `${from?.name || "Origen"} → ${to?.name || "Destino"}` : account?.name || "—"}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 950 }}>{selectedAccountId ? signedMoney(impactForAccount(row, selectedAccountId, selectedAccount?.currency)) : `${row.type === "expense" ? "-" : row.type === "income" ? "+" : ""}${money(row.amount, row.currency)}`}</TableCell>
                    <TableCell><IconButton size="small" onClick={() => openMovementEditor(row)}><EditRoundedIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>;
                })}</TableBody>
              </Table>
            </TableContainer>
            {!filteredTransactions.length ? <Typography sx={{ color: "text.secondary", py: 3, textAlign: "center" }}>No hay movimientos para mostrar.</Typography> : null}
          </CardContent>
        </Card>

        <Stack spacing={2}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}><Stack direction="row" spacing={.7} alignItems="center"><AccountBalanceWalletRoundedIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 950 }}>Cajas y cuentas</Typography></Stack><Button size="small" startIcon={<AddRoundedIcon />} onClick={openNewAccount} sx={{ textTransform: "none", fontWeight: 900 }}>Nueva</Button></Stack>
            <Typography sx={{ mt: .4, mb: 1.2, color: "text.secondary", fontSize: ".78rem" }}>Renombrar una caja conserva todos sus movimientos y saldos porque el vínculo es por ID.</Typography>
            <Stack divider={<Divider flexItem />} spacing={0}>{activeAccounts.map((row) => <Stack key={idOf(row)} direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ py: .9 }}><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900 }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".7rem", textTransform: "uppercase" }}>{row.type} · {row.currency} · {money(row.balance, row.currency)}</Typography></Box><IconButton size="small" onClick={() => openEditAccount(row)}><EditRoundedIcon fontSize="small" /></IconButton></Stack>)}</Stack>
          </CardContent></Card>

          <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}><Stack direction="row" spacing={.7} alignItems="center"><CategoryRoundedIcon color="primary" /><Typography variant="h6" sx={{ fontWeight: 950 }}>Categorías</Typography></Stack><Button size="small" startIcon={<AddRoundedIcon />} onClick={openNewCategory} sx={{ textTransform: "none", fontWeight: 900 }}>Nueva</Button></Stack>
            <Typography sx={{ mt: .4, mb: 1.2, color: "text.secondary", fontSize: ".78rem" }}>Las detectadas vienen de tu historial. Podés convertirlas en categorías administradas y renombrar el historial de una vez.</Typography>
            <Stack spacing={.65}>{categories.map((row) => <Stack key={idOf(row)} direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ p: .8, borderRadius: 2, bgcolor: "action.hover" }}><Box><Typography sx={{ fontWeight: 900, fontSize: ".82rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".68rem" }}>{CATEGORY_KINDS.find(([value]) => value === row.kind)?.[1] || row.kind}</Typography></Box><IconButton size="small" onClick={() => openEditCategory(row)}><EditRoundedIcon fontSize="small" /></IconButton></Stack>)}</Stack>
            {detectedCategories.length ? <><Divider sx={{ my: 1.2 }} /><Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 900, textTransform: "uppercase", mb: .7 }}>Detectadas en movimientos</Typography><Stack spacing={.55}>{detectedCategories.slice(0, 18).map((row) => <Stack key={`${row.kind}-${row.name}`} direction="row" justifyContent="space-between" alignItems="center" gap={1}><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 850, fontSize: ".8rem" }}>{row.name}</Typography><Typography sx={{ color: "text.secondary", fontSize: ".67rem" }}>{row.uses} usos · {row.kind === "expense" ? "gasto" : "ingreso"}</Typography></Box><Button size="small" onClick={() => openDetectedCategory(row)} sx={{ textTransform: "none", minWidth: 0, fontWeight: 850 }}>Gestionar</Button></Stack>)}</Stack></> : null}
          </CardContent></Card>
        </Stack>
      </Box>

      <OwnerFinanceDialog open={movementOpen} onClose={() => setMovementOpen(false)} accounts={activeAccounts} initialType="expense" onSaved={async () => { setMovementOpen(false); await load(); }} />

      <Dialog open={Boolean(editMovement)} onClose={() => !saving && setEditMovement(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Editar movimiento</DialogTitle>
        <DialogContent><Stack spacing={1.2} sx={{ pt: .5 }}>
          <TextField select label="Categoría" value={movementForm.category} onChange={(e) => setMovementForm({ ...movementForm, category: e.target.value })}><MenuItem value=""><em>Sin categoría</em></MenuItem>{categoryNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</TextField>
          <TextField label="Descripción" value={movementForm.description} onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })} />
          <TextField label="Área" value={movementForm.area} onChange={(e) => setMovementForm({ ...movementForm, area: e.target.value })} />
          <TextField type="datetime-local" label="Fecha y hora" InputLabelProps={{ shrink: true }} value={movementForm.occurredAt} onChange={(e) => setMovementForm({ ...movementForm, occurredAt: e.target.value })} />
          {editMovement?.type !== "transfer" ? <TextField select label="Caja / cuenta" value={movementForm.accountId} onChange={(e) => setMovementForm({ ...movementForm, accountId: e.target.value })}>{activeAccounts.filter((row) => row.currency === editMovement?.currency).map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {row.currency}</MenuItem>)}</TextField> : <Alert severity="info">En transferencias, esta edición rápida conserva origen y destino para no alterar la contabilidad accidentalmente.</Alert>}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setEditMovement(null)} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={saveMovement} disabled={saving}>Guardar cambios</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(accountDialog)} onClose={() => !saving && setAccountDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>{accountDialog?.mode === "edit" ? "Editar caja / cuenta" : "Nueva caja / cuenta"}</DialogTitle>
        <DialogContent><Stack spacing={1.2} sx={{ pt: .5 }}>
          <TextField label="Nombre" value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} autoFocus />
          <TextField select label="Tipo" value={accountForm.type} onChange={(e) => setAccountForm({ ...accountForm, type: e.target.value })}>{ACCOUNT_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          {accountDialog?.mode !== "edit" ? <><TextField label="Moneda" value={accountForm.currency} onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value.toUpperCase() })} /><TextField type="number" label="Saldo inicial" value={accountForm.openingBalance} onChange={(e) => setAccountForm({ ...accountForm, openingBalance: e.target.value })} /></> : <Alert severity="info">La moneda y el saldo inicial no se cambian desde esta edición rápida para no reinterpretar movimientos históricos.</Alert>}
          <TextField label="Notas" multiline minRows={2} value={accountForm.notes} onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} />
          {accountDialog?.mode === "edit" ? (
            <Box sx={{ mt: .4, pt: 1.2, borderTop: "1px solid", borderColor: "divider" }}>
              {!accountDeleteConfirm ? (
                <Button
                  color="error"
                  size="small"
                  onClick={() => setAccountDeleteConfirm(true)}
                  disabled={saving}
                  sx={{ textTransform: "none", fontWeight: 950, px: 0 }}
                >
                  Eliminar cuenta
                </Button>
              ) : (
                <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
                  <Typography sx={{ fontWeight: 950, fontSize: ".84rem" }}>¿Eliminar {accountDialog?.row?.name || "esta cuenta"}?</Typography>
                  <Typography sx={{ mt: .25, fontSize: ".76rem" }}>Dejará de aparecer en Cajas y cuentas. Los movimientos históricos se conservan para no romper tus saldos ni la contabilidad.</Typography>
                  <Stack direction="row" spacing={.8} sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => setAccountDeleteConfirm(false)} disabled={saving} sx={{ textTransform: "none", fontWeight: 900 }}>Conservar</Button>
                    <Button size="small" color="error" variant="contained" onClick={deleteAccount} disabled={saving} sx={{ textTransform: "none", fontWeight: 950 }}>Sí, eliminar</Button>
                  </Stack>
                </Alert>
              )}
            </Box>
          ) : null}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => { setAccountDeleteConfirm(false); setAccountDialog(null); }} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={saveAccount} disabled={saving || !accountForm.name.trim()}>Guardar</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(categoryDialog)} onClose={() => !saving && setCategoryDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>{categoryDialog?.mode === "create" ? "Nueva categoría" : "Editar categoría"}</DialogTitle>
        <DialogContent><Stack spacing={1.2} sx={{ pt: .5 }}>
          {categoryDialog?.mode === "detected" ? <Alert severity="info">Esta categoría existe en movimientos históricos. Al guardarla, pasa a ser administrable y el nuevo nombre se aplica a esos movimientos.</Alert> : null}
          <TextField label="Nombre" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} autoFocus />
          <TextField select label="Se usa para" value={categoryForm.kind} onChange={(e) => setCategoryForm({ ...categoryForm, kind: e.target.value })}>{CATEGORY_KINDS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
          {categoryDialog?.mode === "edit" ? <Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>Si cambiás el nombre, también se actualizarán los movimientos históricos que usaban esta categoría.</Typography> : null}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setCategoryDialog(null)} disabled={saving}>Cancelar</Button><Button variant="contained" onClick={saveCategory} disabled={saving || !categoryForm.name.trim()}>Guardar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
